import { PropertiesFillDialog } from '@pages/files/dialogs/PropertiesFillDialog';
import { LStorage } from '@shared/Storage';
import { DocumentsService } from '@shared/documents/DocumentsService';
import { ImagesService } from '@shared/images/ImagesService';
import { SvgIconsB64 } from '@shared/map/MapIcons';
import { Button } from '@shared/ui-react/Button';
import { ContextMenu } from '@shared/ui-react/ContextMenu';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { getFilenameWithVersion } from '@shared/utils/utils';
import { cs } from '@shared/utils/utils-classname';
import { observer } from 'mobx-react';
import * as React from 'react';
import './DocumentsImagesPanelThumbnails.scss';
import { DocumentFeatureFactory } from '@openlayers/documents/DocumentFeatureFactory';
import { PixelPackerWindow } from '@shared/images/pixelpacker/PixelPackerWindow';

const __ = kuura.getMessage;

@observer
export class DocumentsImagesPanelThumbnails extends React.Component<
    {
        type: 'documents' | 'images';
        items: DocumentTypes[];
        unfilteredItems: DocumentTypes[];
        focusItemId?: number;
        folderId?: number;
        folderName?: string;
        onClick: (documentId: number) => void;
        onDelete?: (item: DocumentTypes) => void;
        onEdit?: (documentId: number) => void;
        onEditSelected?: (docs: DocumentTypes[], activateEditLocation?: boolean) => void;
        onGoToFile?: (uuid: string) => void;
        onClose?: () => void;
        onExpand?: () => void;
        canEdit: boolean;
        canSelect: boolean;
        canDownload?: boolean;
        hasContextMenu?: boolean;
        checkedIds?: number[];
        onChecked?: (ids: number[]) => void;
    },
    {
        checkedIds: number[];
        view: 'thumbs' | 'list' | string;
        isDownloading: boolean;
        showAll: boolean;
    }
> {
    static defaultProps = {
        canDownload: true,
        canSelect: true,
        hasContextMenu: true,
        links: [],
    };

    state = {
        checkedIds: [] as number[],
        view: 'thumbs',
        isDownloading: false,
        showAll: false,
    };

    initialized = false;
    focusedEl: HTMLElement = null;

    componentDidMount() {
        this.initialized = true;

        let targetView = this.props.type === 'documents' ? 'list' : 'thumbs';
        const storedView = LStorage.getItem('map-panel-' + this.props.type + '_thumbnails-view');
        if (['thumbs', 'list'].includes(storedView)) {
            targetView = storedView;
        }
        this.setState({
            view: targetView,
        });

        setTimeout(() => {
            if (this.props.focusItemId && this.focusedEl) {
                this.focusedEl.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
            }
        }, 100);
    }

    componentDidUpdate(prevProps: Readonly<DocumentsImagesPanelThumbnails['props']>) {
        if (prevProps.folderId !== this.props.folderId) {
            if (this.state.showAll) {
                this.setState({ showAll: false });
            }
            if ((this.props.checkedIds || this.state.checkedIds).length > 0) {
                this.selectNone();
            }
        }
    }

    readonly extensionIcons = {
        doc: `data:image/svg+xml;base64,${btoa(unescape(DocumentFeatureFactory.icons.doc))}`,
        xls: `data:image/svg+xml;base64,${btoa(unescape(DocumentFeatureFactory.icons.xls))}`,
    };

    render() {
        if (!this.initialized) return null;

        const { unfilteredItems, canEdit, canSelect, onExpand, onClose, focusItemId, canDownload } = this.props;
        const checkedIds = this.props.checkedIds || this.state.checkedIds;
        const checkedCount = checkedIds.length;
        const isThumbs = this.state.view === 'thumbs';
        const isImages = this.props.type === 'images';
        const isIndeterminate = checkedCount !== 0 && checkedIds.length !== this.availableItems.length;
        const isAllSelected = checkedCount === this.availableItems.length;
        const hiddenCount = (unfilteredItems?.length ?? 0) - (this.availableItems?.length ?? 0);
        const hasExternalSelected = checkedIds.some(id => !!this.availableItems.find(item => item.id == id)?.url);
        this.focusedEl = null;

        const HiddenByFilterBanner = (
            <div className='flex-row ai-center gap-half' style={{ justifyContent: 'center' }}>
                <div>{__('general.n_hidden_by_filter', hiddenCount)}</div>
                <Button secondary small onClick={() => this.setState({ showAll: true })}>
                    {__('general.show_all')}
                </Button>
            </div>
        );

        if (!this.availableItems || this.availableItems.length === 0) {
            return (
                <div className='text-center padded'>
                    <div>{__('general.folder_is_empty')}</div>
                    {hiddenCount > 0 ? HiddenByFilterBanner : null}
                </div>
            );
        }

        return (
            <>
                <nav className='flex-row' style={{ flexShrink: 0, marginBottom: 3, alignItems: 'center' }}>
                    {canEdit || canSelect ? (
                        <div style={{ alignItems: 'center', margin: '3px 8px' }}>
                            <input
                                type='checkbox'
                                title={isAllSelected ? __('general.deselect_all') : __('general.select_all')}
                                onClick={this.selectAllToggle}
                                checked={isAllSelected}
                                readOnly
                                ref={el => el && (el.indeterminate = isIndeterminate)}
                            />
                        </div>
                    ) : this.props.folderName ? (
                        <div className='flex-row ait-center of-h' style={{ flex: 1 }}>
                            <i className='iki iki-folder-closed' style={{ fontSize: '1.2em' }} />
                            &nbsp;
                            <span className='ellipsis'>{this.props.folderName}</span>
                        </div>
                    ) : null}
                    <div className='btn-group'>
                        {canEdit && (
                            <Dropdown disabled={checkedCount < 1} closeOnBodyClick>
                                <div>
                                    {__('general.edit')} {checkedCount ? <span>&nbsp;({checkedCount})</span> : null}
                                </div>
                                <ul className='clickable'>
                                    <li onClick={this.editSelected}>{__('js_files.edit_basic_info')}</li>
                                    {!hasExternalSelected && <li onClick={this.editPropertiesSelected}>{__('js_files.edit_properties')}</li>}
                                </ul>
                            </Dropdown>
                        )}

                        {canDownload && isImages && (
                            <Dropdown disabled={checkedCount < 1 || this.state.isDownloading || hasExternalSelected} closeOnBodyClick>
                                <i className='iki iki-download'></i>
                                <ul className='clickable'>
                                    <li onClick={() => this.downloadSelected(false)}>
                                        <i className='iki iki-download'></i> {__('general.download')}
                                    </li>
                                    <li onClick={() => this.downloadSelected(true)}>
                                        <i className='iki iki-download'></i> {__('js_images.download_annotated_images')} (BETA)
                                    </li>
                                </ul>
                            </Dropdown>
                        )}

                        {canDownload && !isImages && (
                            <Button
                                small
                                onClick={() => this.downloadSelected(false)}
                                loading={this.state.isDownloading}
                                title={__('general.download')}
                                icon='iki iki-download'
                                disabled={checkedCount < 1 || this.state.isDownloading || hasExternalSelected}
                            />
                        )}
                    </div>
                    <div style={{ marginLeft: 'auto' }}>
                        <Button
                            onClick={this.toggleView}
                            icon={isThumbs ? 'iki iki-menu' : 'iki iki-grid'}
                            title={isThumbs ? 'List' : 'Thumbnails'}
                            className='ik-button-secondary--subtle ik-button-small'
                        />
                        {typeof onExpand === 'function' && (
                            <button onClick={onExpand} className='ik-button-secondary--subtle ik-button-small'>
                                <i className='iki iki-expand' />
                            </button>
                        )}
                        {typeof onClose === 'function' && (
                            <button onClick={onClose} className='ik-button-secondary--subtle ik-button-small' title={__('general.close')}>
                                <i className='iki iki-cross' />
                            </button>
                        )}
                    </div>
                </nav>

                {hiddenCount > 0 ? HiddenByFilterBanner : null}

                <div className='scroll-wrapper of-a'>
                    <section
                        className={cs('doc-image-thumbnails__wrapper', {
                            'is-list': !isThumbs,
                            'is-thumbs': isThumbs,
                        })}
                    >
                        {this.availableItems.map((item, index) => {
                            const hasPosition = item.geographicPoint && item.geographicPoint.lat != 0;

                            const ext = utils.getExtensionFromFilename(item.name).toLowerCase();
                            let iconPath = SvgIconsB64.blank;
                            let iconScale = 1;

                            if (this.props.type === 'images') {
                                iconPath = `${kuura.getContextPath()}/api/1/image/${item.id}_s.jpg`;
                            } else if (SvgIconsB64[item.icon]) {
                                iconPath = SvgIconsB64[item.icon];
                                iconScale = 0.7;
                            } else if (ext === 'pdf') {
                                iconPath = SvgIconsB64.pdf;
                            }
                            if (ext === 'doc' || ext === 'docx') {
                                iconPath = this.extensionIcons.doc;
                            } else if (ext === 'xls' || ext === 'xlsx') {
                                iconPath = this.extensionIcons.xls;
                            }

                            const isContextMenuEmpty = this.getContextMenuItems(item).length === 0;

                            const isChecked = checkedIds.includes(item.id);

                            return (
                                <ContextMenu
                                    key={item.id + '_' + (item.linkId ?? '')}
                                    render={isContextMenuEmpty ? null : () => this.renderContextMenu(item)}
                                    disabled={!this.props.hasContextMenu}
                                >
                                    <div
                                        onClick={e => this.handleThumbnailClick(e, item.id)}
                                        onMouseDown={e => this.handleMouseClick(e, item)}
                                        className={cs('doc-image-thumbnails__item thumb', {
                                            inactive: checkedCount > 0 && !isChecked,
                                            focused: focusItemId == item.id && !item.linkId,
                                            'stretch-thumb-img': isImages,
                                        })}
                                        ref={el => (focusItemId == item.id ? (this.focusedEl = el) : null)}
                                        title={ImagesService.getInfoTooltip(item)}
                                    >
                                        {isThumbs && (
                                            <>
                                                <img src={iconPath} loading='lazy' style={{ objectFit: isImages ? 'cover' : 'none' }} />
                                                {item.linkId && <i className='iki iki-shortcut' style={{ fontSize: '1.3em', position: 'absolute', bottom: 5, right: 5 }} />}
                                            </>
                                        )}

                                        {(canEdit || canSelect) && (
                                            <input
                                                type='checkbox'
                                                checked={isChecked}
                                                onChange={e => this.handleSelectionCheckbox(item.id, !isChecked)}
                                                title={__('general.select_for_editing')}
                                            />
                                        )}

                                        {!isThumbs && (
                                            <>
                                                <span className='thumb-small'>
                                                    <img
                                                        src={iconPath}
                                                        loading='lazy'
                                                        style={{ objectFit: isImages ? 'cover' : 'none', transform: iconScale === 1 ? 'none' : `scale(${iconScale})` }}
                                                    />
                                                    {item.linkId && <i className='iki iki-shortcut' style={{ fontSize: '1.3em', position: 'absolute', bottom: 0, right: 0 }} />}
                                                </span>
                                                <span className='item-name ellipsis'>{getFilenameWithVersion(item)}</span>
                                            </>
                                        )}

                                        {this.props.hasContextMenu && !isContextMenuEmpty && (
                                            <Dropdown className='ik-button-secondary--subtle item-more-dropdown' closeOnBodyClick={true}>
                                                <i className='iki iki-more'></i>
                                                {this.renderContextMenu(item)}
                                            </Dropdown>
                                        )}

                                        {!hasPosition && (
                                            <Button
                                                subtle
                                                secondary
                                                icon='iki iki-pin'
                                                tooltip={`${__('general.validation.location_is_missing')}. ${canEdit ? __('general.click_to_edit') : ''}`}
                                                iconSize={18}
                                                disabled={!canEdit}
                                                onClick={() => this.openEditLocation(item)}
                                            />
                                        )}
                                    </div>
                                </ContextMenu>
                            );
                        })}
                    </section>
                </div>
            </>
        );
    }

    private getContextMenuItems = (item: DocumentTypes) => {
        const isExternal = !!item.url;
        return [
            this.props.canEdit ? 'edit' : null,
            this.props.canEdit && !isExternal ? 'edit-properties' : null,
            this.props.canDownload && !isExternal ? 'download' : null,
            item.linkId ? 'go-to-file' : null,
            'open-in-new-tab',
            this.props.canEdit ? 'delete' : null,
        ].filter(Boolean);
    };

    private renderContextMenu = (item: DocumentTypes) => {
        const editor = this.props.type === 'images' ? ImagesService : DocumentsService;
        const items = this.getContextMenuItems(item);
        return (
            <ul className='clickable'>
                {items.includes('edit') && <li onClick={() => this.props.onEditSelected([item])}>{__('js_files.edit_basic_info')}</li>}
                {items.includes('edit-properties') && <li onClick={() => this.openEditPropertiesDialog([item])}>{__('js_files.edit_properties')}</li>}
                {items.includes('download') && <li onClick={() => editor.download([item.id], item.name)}>{__('general.download')}</li>}
                {items.includes('open-in-new-tab') && (
                    <li
                        onClick={() => {
                            if (item.url) {
                                window.open(item.url, '_blank');
                            } else {
                                editor.openInNewTab(item.id, item.name);
                            }
                        }}
                    >
                        {__('general.open_in_new_tab')}
                    </li>
                )}
                {this.props.onGoToFile && items.includes('go-to-file') && <li onClick={() => this.props.onGoToFile(item.uuid)}>{__('js_files.go_to_file')}</li>}
                {items.includes('delete') && (
                    <li onClick={() => this.props.onDelete(item)} style={{ color: '#FF473D' }}>
                        {__(item.linkId ? 'js_link.remove_btn' : 'general.delete')}
                    </li>
                )}
            </ul>
        );
    };

    private readonly handleSelectionCheckbox = (documentId: number, isChecked?) => {
        let checkedIds = [...(this.props.checkedIds || this.state.checkedIds)];

        if (typeof isChecked == 'undefined') {
            // toggle
            isChecked = !checkedIds.includes(documentId);
        }

        if (isChecked) {
            checkedIds.push(documentId);
        } else {
            checkedIds = checkedIds.filter(i => i != documentId);
        }

        if (typeof this.props.onChecked === 'function') {
            this.props.onChecked(checkedIds);
        } else {
            this.setState({
                checkedIds,
            });
        }
    };

    private readonly handleThumbnailClick = (e, documentId: number) => {
        if (['INPUT', 'I', 'BUTTON', 'LI'].includes(e.target.nodeName)) return; // dont fire click handler if checkbox was clicked
        this.props.onClick(documentId);
    };

    private readonly editSelected = () => {
        const selectedItems: DocumentTypes[] = this.state.checkedIds.map(id => this.availableItems.find(i => i.id === id));
        this.props.onEditSelected(selectedItems);
    };

    private readonly editPropertiesSelected = () => {
        const selectedItems: DocumentTypes[] = this.state.checkedIds.map(id => this.availableItems.find(i => i.id === id));
        this.openEditPropertiesDialog(selectedItems);
    };

    private readonly downloadSelected = (annotated = false) => {
        if (annotated && this.props.type === 'images') {
            const uuids = this.state.checkedIds.map(id => this.availableItems.find(i => i.id === id).uuid);
            PixelPackerWindow.open(uuids, true);
        } else {
            this.setState({ isDownloading: true });
            const editor = this.props.type === 'images' ? ImagesService : DocumentsService;
            editor.download(this.state.checkedIds, null).always(() => this.setState({ isDownloading: false }));
        }
    };

    private readonly selectAll = () => {
        const checkedIds = new Set<number>();
        for (const image of this.availableItems) {
            checkedIds.add(image.id);
        }

        if (typeof this.props.onChecked === 'function') {
            this.props.onChecked([...checkedIds]);
        } else {
            this.setState({
                checkedIds: [...checkedIds],
            });
        }
    };

    private readonly selectNone = () => {
        if (typeof this.props.onChecked === 'function') {
            this.props.onChecked([]);
        } else {
            this.setState({
                checkedIds: [],
            });
        }
    };

    private selectAllToggle = () => {
        if ((this.props.checkedIds || this.state.checkedIds).length === 0) this.selectAll();
        else this.selectNone();
    };

    private openEditPropertiesDialog = (items: DocumentTypes[]) => {
        PropertiesFillDialog.openForFolder(
            items[0].folderUuid,
            items.map(i => i.uuid)
        );
    };

    private toggleView = () => {
        this.setState(
            {
                view: this.state.view === 'thumbs' ? 'list' : 'thumbs',
            },
            () => {
                LStorage.setItem('map-panel-' + this.props.type + '_thumbnails-view', this.state.view);
            }
        );
    };

    private handleMouseClick = (e: React.MouseEvent, item: IImage | IDocument) => {
        if (e.button === 1) {
            // Middle click
            e.preventDefault();
            if (this.props.type === 'images') {
                window.open(kuura.getContextPath() + '/api/1/image/' + item.id + '_o.jpg', '_blank');
            } else {
                if (item.url) {
                    window.open(item.url, '_blank');
                } else {
                    window.open(kuura.getContextPath() + '/api/1/file/' + item.id + '/get?inline=true', '_blank');
                }
            }
        }
    };

    private openEditLocation = (item: DocumentTypes) => {
        this.props.onEditSelected([item], true);
    };

    get availableItems() {
        return this.state.showAll ? this.props.unfilteredItems : this.props.items;
    }
}
